'use client';

import { ArrowForward, Bolt, Close } from '@mui/icons-material';
import {
  Box,
  Button,
  Container,
  GlobalStyles,
  IconButton,
  InputAdornment,
  InputBase,
  Link as MuiLink,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ChangeEvent, ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import { ScrollDepthTracker } from '@/components/analytics/ScrollDepthTracker';
import GlobalBlockRenderer from '@/components/ContentBlocks/GlobalBlockRenderer/GlobalBlockRenderer';
import { CustomBackground } from '@/components/CustomBackground/CustomBackground';
import { useSiteSettings } from '@/contexts/SiteSettingsContext';
import { buildUrlWithAmount, formatNumberWithCommas } from '@/lib/helpers/formatHelpers';
import { getPageCodeFromPath } from '@/lib/helpers/pageHelpers';
import { buildUrlWithUtmParams } from '@/lib/helpers/utmParamHelpers';
import {
  ComponentBlocksHomepageHeroForm,
  ComponentDataHomepageContent,
  Enum_Componentsharedbackground_Backgroundcolor,
  Review,
} from '@/types';

import { BorrowingMadeEasySection } from './BorrowingMadeEasySection';
import { FinalCtaSection } from './FinalCtaSection';
import { HowItWorksSection } from './HowItWorksSection';
import { LendersTrustedSection } from './LendersTrustedSection';
import { newHomepageTypography } from './newHomepageStyles';
import { TestimonialsSection } from './TestimonialsSection';

const REVIEWS_IO_URL = 'https://www.reviews.io/company-reviews/store/lend-com-au';

const LENDER_MATCH_LOGOS: { src: string; insetPct?: number }[] = [
  { src: '/assets/new-homepage/lenders/shift.svg' },
  { src: '/assets/new-homepage/lenders/prospa2023.svg', insetPct: 5 },
  { src: '/assets/new-homepage/lenders/lumi.svg' },
  { src: '/assets/new-homepage/lenders/pepper.svg', insetPct: 0 },
];

const GAUGE_CENTER = { x: 221.4, y: 273.2 };
const GAUGE_TICK_RADIUS = { inner: 124, outer: 151 };
const GAUGE_TICK_ANGLE_DEG = { start: -178.44, end: -1.56 };
const GAUGE_TICK_COUNT = 132;

// The black "filled" arc is revealed with an angular wedge clip whose leading
// edge sweeps at exactly the same angular rate as the needle, so the fill edge
// stays locked under the arrow instead of racing ahead of it (a horizontal clip
// would reveal arc-length non-linearly vs. the radial needle).
// Start angle = needle direction at progress 0 (far-left of the arc); sweep
// matches the needle's 130° rotation, with a tiny lead buffer so the arc's end
// cap is never clipped at the 100% frame. Radius just needs to exceed the band.
const GAUGE_FILL_START_ANGLE_DEG = -179.55;
const GAUGE_FILL_SWEEP_DEG = 131.5;
const GAUGE_WEDGE_RADIUS = 260;

// Single master clock for the whole widget. Returns one eased 0→1 progress so
// every animated element (needle, arc fill, and all the count-up numbers) is a
// pure function of the same value and therefore moves — and finishes — in lockstep.
// Previously each number ran its own rAF loop, which let them drift apart.
function useGaugeProgress(durationMs = 1800, active = true) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!active) {
      setProgress(0);
      return;
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setProgress(1);
      return;
    }

    let animationFrame = 0;
    let startTime: number | null = null;

    const tick = (timestamp: number) => {
      startTime ??= timestamp;
      const elapsed = timestamp - startTime;
      const linear = Math.min(elapsed / durationMs, 1);
      const eased = 1 - Math.pow(1 - linear, 3);

      setProgress(eased);

      if (linear < 1) {
        animationFrame = window.requestAnimationFrame(tick);
      } else {
        setProgress(1);
      }
    };

    animationFrame = window.requestAnimationFrame(tick);

    return () => {
      window.cancelAnimationFrame(animationFrame);
    };
  }, [active, durationMs]);

  return progress;
}

function BorrowingPowerPreview() {
  const theme = useTheme();
  const [isWidgetReady, setIsWidgetReady] = useState(false);
  const widgetImageRef = useRef<HTMLImageElement | null>(null);
  const progress = useGaugeProgress(1800, isWidgetReady);
  const preApprovedAmount = Math.round(87590 * progress);
  const borrowingPower = Math.round(89 * progress);
  const lendMaxAmount = Math.round(97990 * progress);
  const repaymentAmount = Math.round(1870 * progress);
  const approvalLikelihood = Math.round(96 * progress);
  // Needle + arc fill ride the raw (unrounded, continuous) progress so the
  // arrow sweeps smoothly instead of stepping through 89 integer positions.
  const borrowingProgress = progress;
  const [lenderIndex, setLenderIndex] = useState(0);

  useEffect(() => {
    if (widgetImageRef.current?.complete) {
      setIsWidgetReady(true);
    }
  }, []);

  useEffect(() => {
    if (!isWidgetReady) return;
    const id = window.setInterval(() => {
      setLenderIndex((i) => (i + 1) % LENDER_MATCH_LOGOS.length);
    }, 2200);
    return () => window.clearInterval(id);
  }, [isWidgetReady]);

  // Pie-wedge clip that reveals the filled arc up to the needle's current angle.
  // Its leading radial edge sweeps at the same angular rate as the needle, so the
  // black fill stays locked under the arrow for the whole travel.
  const fillStartRad = (GAUGE_FILL_START_ANGLE_DEG * Math.PI) / 180;
  const fillLeadRad =
    ((GAUGE_FILL_START_ANGLE_DEG + GAUGE_FILL_SWEEP_DEG * borrowingProgress) * Math.PI) / 180;
  const gaugeFillWedgePath = [
    `M ${GAUGE_CENTER.x} ${GAUGE_CENTER.y}`,
    `L ${GAUGE_CENTER.x + GAUGE_WEDGE_RADIUS * Math.cos(fillStartRad)} ${
      GAUGE_CENTER.y + GAUGE_WEDGE_RADIUS * Math.sin(fillStartRad)
    }`,
    `A ${GAUGE_WEDGE_RADIUS} ${GAUGE_WEDGE_RADIUS} 0 0 1 ${
      GAUGE_CENTER.x + GAUGE_WEDGE_RADIUS * Math.cos(fillLeadRad)
    } ${GAUGE_CENTER.y + GAUGE_WEDGE_RADIUS * Math.sin(fillLeadRad)}`,
    'Z',
  ].join(' ');

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          width: { xs: 'min(100%, 330px)', sm: 390, md: 444 },
          aspectRatio: '444 / 694',
          maxWidth: '100%',
          position: 'relative',
          '& img': {
            userSelect: 'none',
          },
        }}
      >
        <Box
          component="img"
          ref={widgetImageRef}
          src="/assets/new-homepage/borrowing-power-widget-figma.svg?v=4"
          alt="Borrowing power estimate"
          onLoad={() => setIsWidgetReady(true)}
          sx={{
            width: '100%',
            height: '100%',
            display: 'block',
          }}
        />

        <Box
          component="svg"
          viewBox="0 0 444 694"
          aria-hidden="true"
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            overflow: 'visible',
            opacity: isWidgetReady ? 1 : 0,
          }}
        >
          <defs>
            <clipPath id="borrowingPowerProgressClip">
              <path d={gaugeFillWedgePath} />
            </clipPath>
            <linearGradient
              id="borrowingGaugeMarkGradient"
              x1="324.365"
              y1="222.403"
              x2="115.035"
              y2="219.709"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0.0528846" stopColor="#858585" />
              <stop offset="0.923077" stopColor="#ffffff" />
            </linearGradient>
          </defs>
          <g
            opacity="0.93"
            style={{ mixBlendMode: 'multiply' }}
            clipPath="url(#borrowingPowerProgressClip)"
          >
            <path
              d="M275.521 186.246C259.586 176.3 240.682 170.512 220.351 170.429C162.544 170.193 115.081 216.158 114.348 273.084"
              stroke="url(#borrowingGaugeMarkGradient)"
              strokeWidth="39.723"
              strokeMiterlimit={10}
              strokeLinecap="square"
              fill="none"
            />
          </g>
          <g opacity="0.55">
            {Array.from({ length: GAUGE_TICK_COUNT }, (_, i) => {
              const startAngleDeg = GAUGE_TICK_ANGLE_DEG.start;
              const endAngleDeg = GAUGE_TICK_ANGLE_DEG.end;
              const angleDeg =
                startAngleDeg + ((endAngleDeg - startAngleDeg) * i) / (GAUGE_TICK_COUNT - 1);
              const angleRad = (angleDeg * Math.PI) / 180;
              const innerR = GAUGE_TICK_RADIUS.inner;
              const outerR = GAUGE_TICK_RADIUS.outer;
              const cx = GAUGE_CENTER.x;
              const cy = GAUGE_CENTER.y;
              return (
                <line
                  key={i}
                  x1={cx + innerR * Math.cos(angleRad)}
                  y1={cy + innerR * Math.sin(angleRad)}
                  x2={cx + outerR * Math.cos(angleRad)}
                  y2={cy + outerR * Math.sin(angleRad)}
                  stroke="#050506"
                  strokeWidth="1"
                  strokeLinecap="butt"
                />
              );
            })}
          </g>
          <path
            opacity="0.93"
            d="M99.6561 269.511C99.5437 278.249 92.3685 285.304 83.6299 285.268C74.8913 285.233 67.8984 278.12 68.0108 269.382L83.8335 269.447L99.6561 269.511ZM313.591 171.008L302.868 182.736C281.664 163.385 253.28 151.506 221.887 151.378L222.091 135.557L222.294 119.735C261.737 119.896 297.552 134.856 324.315 159.279L313.591 171.008ZM222.091 135.557L221.887 151.378C155.001 151.105 100.496 204.229 99.6561 269.511L83.8335 269.447L68.0108 269.382C69.0816 186.17 138.388 119.393 222.294 119.735L222.091 135.557Z"
            fill="#050506"
            clipPath="url(#borrowingPowerProgressClip)"
          />
          <path
            d="M300.977 168.74L311.91 178.05L226.779 283.556L210.38 269.591L300.977 168.74Z"
            fill="#fff"
            transform={`rotate(${(borrowingProgress - 1) * 130} 221 271)`}
          />
        </Box>

        <Typography
          sx={{
            position: 'absolute',
            left: '14.4%',
            top: '46.8%',
            color: 'brand.bodyText',
            fontSize: { xs: 13, sm: 15, md: 16 },
            fontWeight: 400,
            lineHeight: 1.4,
          }}
        >
          Pre-approved
        </Typography>
        <Typography
          aria-label={`Pre-approved $${preApprovedAmount.toLocaleString()}`}
          sx={{
            position: 'absolute',
            left: '14.5%',
            top: '50.8%',
            color: '#050506',
            fontSize: { xs: 22, sm: 26, md: 28 },
            fontWeight: 900,
            letterSpacing: '0.02em',
            lineHeight: 1.4,
          }}
        >
          ${preApprovedAmount.toLocaleString()}
        </Typography>
        <Typography
          sx={{
            position: 'absolute',
            left: '59.2%',
            top: '46%',
            width: '26.1%',
            color: 'brand.bodyText',
            fontSize: { xs: 13, sm: 15, md: 16 },
            fontWeight: 400,
            lineHeight: 1.4,
            textAlign: 'right',
          }}
        >
          Of max
        </Typography>
        <Typography
          aria-label={`Borrowing power ${borrowingPower}%`}
          sx={{
            position: 'absolute',
            left: '59.2%',
            top: '49.4%',
            width: '26.1%',
            color: '#050506',
            fontSize: { xs: 43, sm: 50, md: 54 },
            fontWeight: 900,
            letterSpacing: '0.02em',
            lineHeight: 1,
            textAlign: 'right',
          }}
        >
          {borrowingPower}%
        </Typography>
        <Box
          sx={{
            position: 'absolute',
            left: '12.8%',
            top: '62%',
            width: '29%',
            height: '3.3%',
            backgroundColor: '#171717',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            left: '53.2%',
            top: '62%',
            width: '29.5%',
            height: '3.3%',
            backgroundColor: '#1f1f1f',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            left: '12.8%',
            top: '77.6%',
            width: '35.5%',
            height: '3.4%',
            backgroundColor: '#171717',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            left: '51.9%',
            top: '77.4%',
            width: '35.5%',
            height: '3.8%',
            backgroundColor: '#171717',
          }}
        />
        <Typography
          sx={{
            position: 'absolute',
            left: '13%',
            top: '62.5%',
            width: '33.6%',
            color: theme.palette.common.white,
            fontSize: { xs: 12, sm: 14, md: 15 },
            fontWeight: 400,
            lineHeight: 1.4,
          }}
        >
          LendMAX
        </Typography>
        <Typography
          sx={{
            position: 'absolute',
            left: '13.9%',
            top: '66.8%',
            color: theme.palette.common.white,
            fontSize: { xs: 19, sm: 23, md: 26 },
            fontWeight: 900,
            lineHeight: 1,
          }}
        >
          ${lendMaxAmount.toLocaleString()}
        </Typography>
        <Typography
          sx={{
            position: 'absolute',
            left: '53.6%',
            top: '62.5%',
            width: '33.6%',
            color: theme.palette.common.white,
            fontSize: { xs: 12, sm: 14, md: 15 },
            fontWeight: 400,
            lineHeight: 1.4,
          }}
        >
          Repayment (p/m)
        </Typography>
        <Typography
          sx={{
            position: 'absolute',
            left: '53.6%',
            top: '66.8%',
            color: theme.palette.common.white,
            fontSize: { xs: 19, sm: 23, md: 26 },
            fontWeight: 900,
            lineHeight: 1,
          }}
        >
          ${repaymentAmount.toLocaleString()}
        </Typography>
        <Typography
          sx={{
            position: 'absolute',
            left: '13%',
            top: '78.2%',
            width: '34.7%',
            color: theme.palette.common.white,
            fontSize: { xs: 12, sm: 14, md: 15 },
            fontWeight: 400,
            lineHeight: 1.4,
          }}
        >
          Best Lender Match
        </Typography>
        <Box
          sx={{
            position: 'absolute',
            left: '12%',
            top: '82%',
            width: '30%',
            height: '9%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {LENDER_MATCH_LOGOS.map((logo, i) => {
            const insetPct = logo.insetPct ?? 15;
            const sizePct = 100 - 2 * insetPct;
            return (
              <Box
                key={logo.src}
                component="img"
                src={logo.src}
                alt=""
                sx={{
                  position: 'absolute',
                  inset: `${insetPct}%`,
                  width: `${sizePct}%`,
                  height: `${sizePct}%`,
                  objectFit: 'contain',
                  opacity: lenderIndex === i ? 1 : 0,
                  transition: 'opacity 500ms ease-in-out',
                  filter: 'brightness(0) invert(1)',
                }}
              />
            );
          })}
        </Box>
        <Typography
          sx={{
            position: 'absolute',
            left: '52.2%',
            top: '78.2%',
            width: '34.7%',
            color: theme.palette.common.white,
            fontSize: { xs: 12, sm: 14, md: 15 },
            fontWeight: 400,
            lineHeight: 1.4,
            textAlign: 'center',
          }}
        >
          Approval Likelihood
        </Typography>
        <Box
          sx={{
            position: 'absolute',
            left: '61.4%',
            top: '80.9%',
            width: '18.1%',
            aspectRatio: '1 / 1',
            borderRadius: '50%',
            backgroundColor: '#171717',
          }}
        />
        <Box
          component="svg"
          viewBox="0 0 62 62"
          aria-hidden="true"
          sx={{
            position: 'absolute',
            left: '62.8%',
            top: '81.9%',
            width: '15.3%',
            aspectRatio: '1 / 1',
            pointerEvents: 'none',
            transform: 'rotate(-90deg)',
          }}
        >
          <circle
            cx="31"
            cy="31"
            r="27"
            fill="none"
            stroke="rgba(32, 31, 27, 0.55)"
            strokeWidth="6"
          />
          <circle
            cx="31"
            cy="31"
            r="27"
            fill="none"
            stroke={theme.palette.brand.green}
            strokeWidth="6"
            strokeLinecap="butt"
            pathLength={100}
            strokeDasharray={`${approvalLikelihood} 100`}
          />
        </Box>
        <Typography
          sx={{
            position: 'absolute',
            left: '62.8%',
            top: '81.9%',
            width: '15.3%',
            aspectRatio: '1 / 1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            color: theme.palette.brand.green,
            fontSize: { xs: 12, sm: 14, md: 15 },
            fontWeight: 900,
            lineHeight: 1,
          }}
        >
          {approvalLikelihood}%
        </Typography>
      </Box>
    </Box>
  );
}

function AvatarStack() {
  const theme = useTheme();
  const avatars = [
    '/assets/new-homepage/hero-avatar-1-v2.webp',
    '/assets/new-homepage/hero-avatar-2-v2.webp',
    '/assets/new-homepage/hero-avatar-3-v2.webp',
  ];

  return (
    <MuiLink
      href={REVIEWS_IO_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Read Lend reviews on Reviews.io"
      sx={{
        display: 'flex',
        alignItems: 'center',
        textDecoration: 'none',
        flexShrink: 0,
      }}
    >
      {avatars.map((src, index) => (
        <Box
          key={src}
          component="img"
          src={src}
          alt=""
          aria-hidden="true"
          sx={{
            width: 31,
            height: 31,
            ml: index === 0 ? 0 : -1.1,
            borderRadius: '50%',
            border: `2px solid ${theme.palette.common.white}`,
            display: 'block',
            objectFit: 'cover',
            boxShadow: '0 2px 8px rgba(19, 13, 44, 0.12)',
          }}
        />
      ))}
    </MuiLink>
  );
}

interface NewHomepageProps {
  reviews?: Review[];
  children?: ReactNode;
  pageHero?: ComponentBlocksHomepageHeroForm | null;
  customContent?: ComponentDataHomepageContent | null;
  showStaticCTA?: boolean;
}

type ReviewsSummary = {
  average: number;
  count: number;
  verdict: string;
};

function formatHomepageAmount(amount?: string | null) {
  const normalizedAmount = (amount || '85000').replace(/,/g, '').replace(/[^0-9.]/g, '');
  return formatNumberWithCommas(normalizedAmount || '85000');
}

export function NewHomepage({
  reviews = [],
  children,
  pageHero,
  customContent,
  showStaticCTA = true,
}: NewHomepageProps) {
  const theme = useTheme();
  const pathname = usePathname();
  const { siteSettings } = useSiteSettings();

  // ── Hero text with CMS fallbacks ─────────────────────────────────────────
  const heroTitleMain = pageHero?.HeroTitleMain || 'Find your Business Loan.';
  const heroTitleAccent = pageHero?.HeroTitleAccent || 'Fast.';
  const heroBody = pageHero?.HeroBody ?? null; // null → render hardcoded rich JSX fallback
  const heroCreditNote = pageHero?.HeroCreditNote || 'Your credit score will not be affected.';
  const heroCtaButtonText = pageHero?.HeroCTA?.[0]?.ButtonText || 'Get Best Loan';
  const heroMatchLabel = pageHero?.HeroMatchLabel || "We'll match you";
  const heroMatchSuffix =
    pageHero?.HeroMatchSuffix || 'with Lenders & Get your Borrowing Power Score';
  const heroLenderCount = pageHero?.HeroLenderCount || '80+';
  const heroLenderLabel = pageHero?.HeroLenderLabel || 'Lender Partners';
  // ─────────────────────────────────────────────────────────────────────────

  const [rawAmount, setRawAmount] = useState(() =>
    formatHomepageAmount(
      pageHero?.HeroCTA?.[0]?.ButtonPlaceholder ?? siteSettings?.HomepageCtaDefaultAmount
    )
  );
  const [reviewsSummary, setReviewsSummary] = useState<ReviewsSummary | null>(null);

  useEffect(() => {
    let isMounted = true;

    fetch('/api/reviews')
      .then((response) => {
        if (!response.ok) throw new Error(`Reviews API error: ${response.status}`);
        return response.json() as Promise<ReviewsSummary>;
      })
      .then((summary) => {
        if (isMounted && Number.isFinite(summary.average)) {
          setReviewsSummary(summary);
        }
      })
      .catch(() => {
        if (isMounted) setReviewsSummary(null);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const userRating = reviewsSummary?.average.toFixed(1) ?? '4.9';

  const finalHref = useMemo(() => {
    const baseUrl =
      pageHero?.HeroCTA?.[0]?.ButtonLink ||
      siteSettings?.HomepageCtaActionUrl ||
      siteSettings?.GetStartedUrl ||
      process.env.NEXT_PUBLIC_FULL_APP_URL ||
      '#';
    const withAmount = buildUrlWithAmount(baseUrl, rawAmount.replace(/,/g, ''));
    return buildUrlWithUtmParams(withAmount, getPageCodeFromPath(pathname));
  }, [
    pathname,
    pageHero?.HeroCTA,
    rawAmount,
    siteSettings?.GetStartedUrl,
    siteSettings?.HomepageCtaActionUrl,
  ]);

  const handleAmountChange = (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value.replace(/,/g, '').replace(/[^0-9.]/g, '');
    const parts = input.split('.');
    if (parts.length > 2 || parts[1]?.length > 2) return;
    setRawAmount(formatNumberWithCommas(input));
  };
  const handleClearAmount = () => setRawAmount('');

  return (
    <>
      <ScrollDepthTracker feature="homepage" />
      <Box
        sx={{
          background: 'linear-gradient(180deg, #fbfbff 0%, #fff 54%, #f8f8ff 100%)',
          color: theme.palette.brand.bodyText,
        }}
      >
        <GlobalStyles
          styles={{
            html: {
              overscrollBehaviorY: 'none',
            },
            body: {
              overscrollBehaviorY: 'none',
              backgroundColor: '#fbfbff',
            },
          }}
        />
        <Box
          component="section"
          sx={{
            position: 'relative',
            overflow: 'hidden',
            pt: { xs: 'calc(72px + 20px)', md: 'calc(116px + 28px)' },
            pb: { xs: 7, md: 10 },
            '&::before': {
              content: '""',
              display: { xs: 'none', md: 'block' },
              position: 'absolute',
              inset: 0,
              backgroundImage: "url('/assets/new-homepage/hero-ribbon.webp')",
              backgroundSize: '100% auto',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'top center',
              transform: 'translate(15%, -10%)',
              maskImage: 'linear-gradient(to bottom, black 0%, black 45%, transparent 85%)',
              WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black 45%, transparent 85%)',
              pointerEvents: 'none',
            },
            '&::after': {
              content: '""',
              display: { xs: 'none', md: 'block' },
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              left: '50%',
              background:
                'radial-gradient(50% 50% at 50% 50%, rgba(223, 179, 247, 0.23) 0%, rgba(179, 170, 223, 0) 100%)',
              pointerEvents: 'none',
            },
          }}
        >
          <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
            <Box
              sx={{
                position: 'relative',
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 360px' },
                columnGap: { md: 5, lg: 8 },
                rowGap: { xs: 4, md: 0 },
                alignItems: 'center',
                minWidth: 0,
                minHeight: { md: 565 },
                maxWidth: 1200,
                mx: 'auto',
              }}
            >
              <Box
                aria-hidden="true"
                sx={{
                  display: { xs: 'none', md: 'block' },
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: -34,
                  height: 370,
                  borderRadius: '40px',
                  background: 'rgba(248, 249, 250, 0.82)',
                  zIndex: 0,
                }}
              />
              <Stack
                spacing={{ xs: 3, md: 4 }}
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  width: '100%',
                  minWidth: 0,
                  maxWidth: { md: 610 },
                  mx: { md: 'auto' },
                  textAlign: { xs: 'center', md: 'left' },
                  alignItems: { xs: 'center', md: 'flex-start' },
                }}
              >
                <Box
                  sx={{
                    pt: { xs: 6, sm: 8, md: 0 },
                  }}
                >
                  <Typography
                    component="h1"
                    sx={{
                      color: theme.palette.brand.bodyText,
                      ...newHomepageTypography.h1,
                      mb: 2.5,
                    }}
                  >
                    {heroTitleMain}{' '}
                    <Box component="span" sx={{ color: theme.palette.brand.primary }}>
                      {heroTitleAccent}
                    </Box>
                  </Typography>
                  {heroBody ? (
                    <Box
                      sx={{
                        maxWidth: 470,
                        '.MuiTypography-root': {
                          color: theme.palette.brand.bodyText,
                          fontSize: { xs: 16, md: 17 },
                          lineHeight: 1.35,
                          mb: 0,
                          pb: 0,
                        },
                      }}
                    >
                      <GlobalBlockRenderer content={heroBody} />
                    </Box>
                  ) : (
                    <Typography
                      sx={{
                        maxWidth: 470,
                        color: theme.palette.brand.bodyText,
                        fontSize: { xs: 16, md: 17 },
                        lineHeight: 1.35,
                      }}
                    >
                      <>
                        Finding the right business loan shouldn&apos;t be a guessing game. We use{' '}
                        <Box component="strong" sx={{ fontWeight: 900 }}>
                          advanced policy-matching technology
                        </Box>{' '}
                        to compare{' '}
                        <Box component="strong" sx={{ fontWeight: 900 }}>
                          over 80 lenders instantly,
                        </Box>{' '}
                        connecting you with the{' '}
                        <Box component="strong" sx={{ fontWeight: 900 }}>
                          best rates
                        </Box>{' '}
                        for your{' '}
                        <Box component="strong" sx={{ fontWeight: 900 }}>
                          unique borrower profile.
                        </Box>
                      </>
                    </Typography>
                  )}
                </Box>

                <Box sx={{ width: '100%', maxWidth: 519 }}>
                  <Typography
                    sx={{ mt: 0.5, mb: 2.5, color: theme.palette.brand.bodyText, fontSize: 16 }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontWeight: 900,
                        backgroundImage: "url('/assets/new-homepage/match_underline.svg')",
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: '0 100%',
                        backgroundSize: '100% 4px',
                        pb: '5px',
                      }}
                    >
                      {heroMatchLabel}
                    </Box>{' '}
                    <Box component="strong" sx={{ fontWeight: 900 }}>
                      {heroMatchSuffix} <Bolt sx={{ fontSize: 14, verticalAlign: '-2px' }} />
                    </Box>
                  </Typography>
                  <Box
                    sx={{
                      width: '100%',
                      borderRadius: '20px',
                      background: alpha(theme.palette.purple[100], 0.78),
                      p: { xs: 2.5, sm: '30px' },
                      boxSizing: 'border-box',
                      textAlign: 'left',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: { xs: 1.4, sm: 2.5 },
                        alignItems: 'stretch',
                      }}
                    >
                      <InputBase
                        value={rawAmount}
                        onChange={handleAmountChange}
                        inputProps={{
                          inputMode: 'decimal',
                          pattern: '[0-9.]*',
                          'aria-label': 'Loan amount',
                        }}
                        startAdornment={
                          <InputAdornment position="start">
                            <Box
                              component="img"
                              src="/assets/new-homepage/search-loan-dollar.svg"
                              alt=""
                              aria-hidden="true"
                              sx={{ width: 32, height: 32, display: 'block' }}
                            />
                          </InputAdornment>
                        }
                        endAdornment={
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="Clear loan amount"
                              edge="end"
                              size="small"
                              onClick={handleClearAmount}
                              onMouseDown={(event) => event.preventDefault()}
                              sx={{
                                color: theme.palette.brand.primary,
                                p: 0.5,
                              }}
                            >
                              <Close sx={{ fontSize: 15 }} />
                            </IconButton>
                          </InputAdornment>
                        }
                        sx={{
                          flex: { xs: '1 1 auto', sm: '0 0 250px' },
                          width: { xs: '100%', sm: 250 },
                          height: 46,
                          pl: 1.3,
                          pr: 2,
                          background: theme.palette.common.white,
                          borderRadius: 1,
                          fontSize: 18,
                          fontWeight: 900,
                          color: theme.palette.brand.bodyText,
                          '& input': { p: 0 },
                        }}
                      />
                      <Button
                        component={Link}
                        href={finalHref}
                        variant="contained"
                        endIcon={<ArrowForward />}
                        sx={{
                          minWidth: { sm: 190 },
                          height: 46,
                          px: { sm: 3.5 },
                          borderRadius: 999,
                          background: theme.palette.brand.primary,
                          fontSize: 13,
                          whiteSpace: 'nowrap',
                          '&:hover,&:focus': {
                            background: theme.palette.brand.brand,
                          },
                        }}
                      >
                        {heroCtaButtonText}
                      </Button>
                    </Box>
                    <Typography sx={{ mt: 1.6, fontSize: 14, fontWeight: 900 }}>
                      {heroCreditNote}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    width: '100%',
                    maxWidth: 780,
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, auto)' },
                    justifyContent: { xs: 'center', md: 'flex-start' },
                    alignItems: 'center',
                    gap: { xs: 2, sm: 4 },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: { xs: 'center', md: 'flex-start' },
                      gap: 1,
                      fontSize: 14,
                    }}
                  >
                    <AvatarStack />
                    <MuiLink
                      href={REVIEWS_IO_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Read Lend reviews on Reviews.io"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.75,
                        flexShrink: 0,
                        color: theme.palette.brand.bodyText,
                        textDecoration: 'none',
                        '&:hover .reviews-rating-text': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      <Box
                        component="img"
                        src="/assets/new-homepage/reviewsio-icon.svg"
                        alt="Reviews.io"
                        sx={{
                          width: 18,
                          height: 18,
                          display: 'block',
                          flexShrink: 0,
                          transform: 'translateY(1px)',
                        }}
                      />
                      <Typography
                        className="reviews-rating-text"
                        sx={{ fontSize: 'inherit', whiteSpace: 'nowrap' }}
                      >
                        User rating{' '}
                        <Box component="strong" sx={{ fontWeight: 900 }}>
                          {userRating}/5.00
                        </Box>
                      </Typography>
                    </MuiLink>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: { xs: 'center', md: 'flex-start' },
                      fontSize: 14,
                    }}
                  >
                    <Typography sx={{ fontSize: 'inherit', whiteSpace: 'nowrap' }}>
                      <Box component="strong" sx={{ fontWeight: 900 }}>
                        {heroLenderCount}
                      </Box>{' '}
                      {heroLenderLabel}
                    </Typography>
                  </Box>
                </Box>
              </Stack>

              <Box
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  transform: { md: 'translateX(-28px)' },
                }}
              >
                <BorrowingPowerPreview />
              </Box>
            </Box>
          </Container>
        </Box>
        {/*wrap each section in CustomBackground for fade effect on scroll*/}
        <CustomBackground
          backgroundOptions={{ FadeInOutContent: true }}
          displayOptions={{
            ContentsWidth: 'extra-wide',
            PaddingTop: 'none',
            PaddingBottom: 'none',
          }}
        >
          <BorrowingMadeEasySection cms={customContent} />
        </CustomBackground>
        <CustomBackground
          backgroundOptions={{ FadeInOutContent: true }}
          displayOptions={{
            ContentsWidth: 'extra-wide',
            PaddingTop: 'none',
            PaddingBottom: 'none',
            PaddingInline: 'none',
          }}
        >
          <HowItWorksSection cms={customContent} />
        </CustomBackground>
        <CustomBackground
          backgroundOptions={{ FadeInOutContent: true }}
          displayOptions={{
            ContentsWidth: 'extra-wide',
            PaddingTop: 'none',
            PaddingBottom: 'none',
          }}
        >
          <TestimonialsSection reviews={reviews} cms={customContent} />
        </CustomBackground>
        <CustomBackground
          backgroundOptions={{ FadeInOutContent: true }}
          displayOptions={{
            ContentsWidth: 'extra-wide',
            PaddingTop: 'none',
            PaddingBottom: 'none',
          }}
        >
          <LendersTrustedSection cms={customContent} />
        </CustomBackground>
        {showStaticCTA && (
          <CustomBackground
            backgroundOptions={{
              FadeInOutContent: true,
              BackgroundColor: Enum_Componentsharedbackground_Backgroundcolor.Primary,
            }}
            displayOptions={{
              ContentsWidth: 'extra-wide',
              PaddingTop: 'none',
              PaddingBottom: 'none',
            }}
          >
            <FinalCtaSection
              rawAmount={rawAmount}
              finalHref={finalHref}
              onAmountChange={handleAmountChange}
            />
          </CustomBackground>
        )}
      </Box>
      {children}
    </>
  );
}
